import { useCallback, useEffect, useId, useRef, useState } from "react";
import { Portal, Text } from "react-native-paper";
import { useAppTheme } from "../../useAppTheme";
import type { TooltipWrapperProps } from "./types";

const DEFAULT_ENTER_TOUCH_DELAY = 200;

const TooltipWrapper = ({
	title,
	children,
	style,
	subtitle,
	display = "inline-block",
	enterTouchDelay = DEFAULT_ENTER_TOUCH_DELAY,
}: TooltipWrapperProps) => {
	const [showTooltip, setShowTooltip] = useState(false);
	const [tooltipPosition, setTooltipPosition] = useState<{
		top: number;
		left: number;
	} | null>(null);
	const triggerRef = useRef<HTMLDivElement>(null);
	const tooltipRef = useRef<HTMLDivElement>(null);
	const paperTheme = useAppTheme();
	const tooltipId = useId();

	// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <explanation>
	const updateTooltipPosition = useCallback(() => {
		if (!triggerRef.current) {
			return;
		}
		if (!tooltipRef.current) {
			return;
		}

		const rect = triggerRef.current.getBoundingClientRect();
		const tooltipRect = tooltipRef.current.getBoundingClientRect();
		const tooltipWidth = tooltipRect.width || 320;
		const tooltipHeight = tooltipRect.height || 100;
		const margin = 8;

		let left = rect.left + rect.width / 2 - tooltipWidth / 2;
		let top = rect.top - margin;

		const viewportWidth = window.innerWidth;
		const minLeft = margin;
		const maxLeft = viewportWidth - tooltipWidth - margin;

		if (left < minLeft) {
			left = minLeft;
		} else if (left > maxLeft) {
			left = maxLeft;
		}

		const spaceAbove = rect.top;
		const spaceBelow = window.innerHeight - rect.bottom;

		if (spaceAbove < tooltipHeight + margin && spaceBelow > spaceAbove) {
			top = rect.bottom + margin;
		} else {
			top = rect.top - tooltipHeight - margin;
		}

		if (top < margin) {
			top = margin;
		} else if (top + tooltipHeight > window.innerHeight - margin) {
			top = window.innerHeight - tooltipHeight - margin;
		}

		setTooltipPosition({ top, left });
	}, []);

	useEffect(() => {
		if (!showTooltip) {
			setTooltipPosition(null);
			return;
		}

		setTooltipPosition({ top: -9999, left: -9999 });

		const handleScroll = () => {
			requestAnimationFrame(() => {
				updateTooltipPosition();
			});
		};
		const handleResize = () => {
			requestAnimationFrame(() => {
				updateTooltipPosition();
			});
		};

		window.addEventListener("scroll", handleScroll, true);
		window.addEventListener("resize", handleResize);

		requestAnimationFrame(() => {
			updateTooltipPosition();
		});

		return () => {
			window.removeEventListener("scroll", handleScroll, true);
			window.removeEventListener("resize", handleResize);
		};
	}, [showTooltip, updateTooltipPosition]);

	useEffect(() => {
		if (showTooltip && tooltipRef.current && tooltipPosition) {
			requestAnimationFrame(() => {
				updateTooltipPosition();
			});
		}
	}, [showTooltip, updateTooltipPosition, tooltipPosition]);

	const handleKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === "Escape" && showTooltip) {
			setShowTooltip(false);
		}
	};

	const handleShow = () => {
		setShowTooltip(true);
	};

	const handleFocus = (e: React.FocusEvent<HTMLDivElement>) => {
		if (e.target.matches(":focus-visible")) {
			setShowTooltip(true);
		}
	};

	const handleHide = () => {
		setShowTooltip(false);
	};

	return (
		<>
			<div
				ref={triggerRef}
				style={{
					cursor: "help",
					display,
					...style,
				}}
				onMouseEnter={handleShow}
				onMouseLeave={handleHide}
				onFocus={handleFocus}
				onBlur={handleHide}
				onKeyDown={handleKeyDown}
				aria-describedby={showTooltip ? tooltipId : undefined}
				// biome-ignore lint/a11y/noNoninteractiveTabindex: tabIndex needed for keyboard accessibility when wrapping non-focusable children for tooltip functionality
				tabIndex={0}
				title={undefined}
			>
				{children}
			</div>
			<Portal>
				<div
					ref={tooltipRef}
					id={tooltipId}
					role="tooltip"
					style={{
						position: "fixed",
						top: tooltipPosition ? `${tooltipPosition.top}px` : "-9999px",
						left: tooltipPosition ? `${tooltipPosition.left}px` : "-9999px",
						visibility: showTooltip && tooltipPosition ? "visible" : "hidden",
						opacity: showTooltip && tooltipPosition ? 1 : 0,
						transform:
							showTooltip && tooltipPosition
								? "translateY(0)"
								: "translateY(4px)",
						transition: "opacity 140ms ease, transform 140ms ease",
						transitionDelay: showTooltip ? `${enterTouchDelay}ms` : "0ms",
						willChange: "opacity, transform",
						backgroundColor: paperTheme.colors.inverseSurface,
						padding: "12px 16px",
						borderRadius: 12,
						maxWidth: 400,
						width: "max-content",
						zIndex: 99999,
						pointerEvents: "none",
						boxShadow: `0px 2px 6px 2px ${paperTheme.colors.shadow}15, 0px 1px 2px 0px ${paperTheme.colors.shadow}30`,
						display: "flex",
						flexDirection: "column",
						gap: 4,
					}}
				>
					{subtitle ? (
						<>
							<Text
								variant="labelMedium"
								style={{ color: paperTheme.colors.inverseOnSurface }}
							>
								{title}
							</Text>
							<Text
								variant="bodySmall"
								style={{ color: paperTheme.colors.inverseOnSurface }}
							>
								{subtitle}
							</Text>
						</>
					) : (
						<Text
							variant="bodySmall"
							style={{ color: paperTheme.colors.inverseOnSurface }}
						>
							{title}
						</Text>
					)}
				</div>
			</Portal>
		</>
	);
};
export default TooltipWrapper;
